(() => {
  "use strict";

  // MISSION_050D_RELIABLE_CART_NAVIGATION

  const BRIDGE_VERSION = "049G-3";
  const CANONICAL_STOREFRONT_ORIGIN = "https://vinyldisorder.com";
  const RETURN_QUEUE_STORAGE_KEY = "chameleon:return-to-queue:v1";

  // A Shopify cart cookie created on a www or legacy storefront alias can be
  // lost when Shopify redirects the cart to the bare production domain. Move
  // the product page into the canonical first-party session before the Studio
  // host performs any Ajax cart work.
  if (
    [
      "www.vinyldisorder.com",
      "vinyl-disorder.com",
      "www.vinyl-disorder.com",
    ].includes(window.location.hostname.toLowerCase())
  ) {
    const canonicalUrl = new URL(window.location.href);
    canonicalUrl.protocol = "https:";
    canonicalUrl.host = new URL(CANONICAL_STOREFRONT_ORIGIN).host;
    window.location.replace(canonicalUrl.toString());
    return;
  }
  const blocks = Array.from(
    document.querySelectorAll("[data-chameleon-shopify-studio]"),
  );

  // MISSION_051K_FULL_BLEED_CUSTOMER_STUDIO
  // The public CHAMELEON page owns the full viewport below the Shopify header.
  // This is scoped only to pages that actually contain the public Studio block.
  const publicBlocks = []; // Template pages own their responsive content height.
  let sizePublicStudio = () => {};

  if (publicBlocks.length > 0) {
    document.documentElement.classList.add("chameleon-public-studio-page");
    document.body.classList.add("chameleon-public-studio-page");

    // MISSION_051K_PUBLIC_MOBILE_HEADER_NORMAL_FLOW
    // Shopify reapplies sticky header classes while scrolling. Keep the exact
    // storefront header section in normal document flow on public Studio pages.
    const syncPublicMobileHeaderFlow = () => {
      const headerSection = document.getElementById("shopify-section-header");
      if (!headerSection) return;
      const isMobile = window.matchMedia("(max-width: 989px)").matches;
      if (isMobile) {
        headerSection.dataset.chameleonMobileHeaderFlow = "true";
        headerSection.style.setProperty("position", "relative", "important");
        headerSection.style.setProperty("top", "auto", "important");
        headerSection.style.setProperty("transform", "none", "important");
        headerSection.classList.remove(
          "shopify-section-header-sticky",
          "shopify-section-header-hidden",
          "animate",
        );
        return;
      }
      if (headerSection.dataset.chameleonMobileHeaderFlow === "true") {
        delete headerSection.dataset.chameleonMobileHeaderFlow;
        headerSection.style.removeProperty("position");
        headerSection.style.removeProperty("top");
        headerSection.style.removeProperty("transform");
      }
    };

    let mobileHeaderSyncFrame = 0;
    const queuePublicMobileHeaderFlow = () => {
      if (mobileHeaderSyncFrame) return;
      mobileHeaderSyncFrame = window.requestAnimationFrame(() => {
        mobileHeaderSyncFrame = 0;
        syncPublicMobileHeaderFlow();
      });
    };

    syncPublicMobileHeaderFlow();
    window.addEventListener("scroll", queuePublicMobileHeaderFlow, { passive: true });
    window.addEventListener("resize", queuePublicMobileHeaderFlow, { passive: true });
    window.addEventListener("orientationchange", queuePublicMobileHeaderFlow, { passive: true });
    window.setTimeout(syncPublicMobileHeaderFlow, 120);
    window.setTimeout(syncPublicMobileHeaderFlow, 600);

    // Phones and tablets keep the stable visual-viewport handoff. Desktop uses
    // the Studio's measured content height so the storefront owns one scroll.
    sizePublicStudio = () => {
      const visualViewport = window.visualViewport;
      const viewportHeight = Math.round(visualViewport?.height || window.innerHeight);
      const phone = window.matchMedia("(max-width: 749px)").matches;
      const desktop = window.matchMedia("(min-width: 1181px)").matches;

      for (const block of publicBlocks) {
        const measuredHeight = Number(block.dataset.chameleonContentHeight || 0);
        const studioHeight = desktop && measuredHeight >= 520
          ? measuredHeight
          : Math.max(phone ? 320 : desktop ? 1100 : 520, viewportHeight);
        block.style.setProperty("--chameleon-studio-height", `${studioHeight}px`);
      }
    };

    sizePublicStudio();
    window.addEventListener("resize", sizePublicStudio, { passive: true });
    window.addEventListener("orientationchange", sizePublicStudio, { passive: true });
    window.visualViewport?.addEventListener("resize", sizePublicStudio, { passive: true });
    window.visualViewport?.addEventListener("scroll", sizePublicStudio, { passive: true });
    window.setTimeout(sizePublicStudio, 120);
    window.setTimeout(sizePublicStudio, 600);
  }
  const REQUIRED_PROPERTIES = [
    "Finished size",
    "Sticker quantity",
    "Material",
    "Laminate",
    "Proof status",
    "_CHAMELEON Checkout Job ID",
    "_CHAMELEON Design ID",
    "_CHAMELEON Proof Status",
    "_CHAMELEON Proof Fingerprint",
    "_CHAMELEON Width",
    "_CHAMELEON Height",
    "_CHAMELEON Sticker Quantity",
    "_CHAMELEON Material",
    "_CHAMELEON Laminate",
    "_CHAMELEON Intended Price Cents",
    "_CHAMELEON Cart Bridge Version",
    "_CHAMELEON Design Summary",
    "_CHAMELEON Original Master SHA256",
    "_CHAMELEON Original Color Mode",
  ];

  const requiredPropertiesFor = (properties) => {
    const pricingVersion = properties["_Pricing version"] || properties["Pricing version"];
    return [
      ...REQUIRED_PROPERTIES,
      properties["_Pricing version"] ? "_Pricing version" : "Pricing version",
      ...(String(pricingVersion || "").startsWith("DEAL-")
        ? ["_CHAMELEON Deal Handle"]
        : []),
    ];
  };

  const ALLOWED_PROPERTIES = new Set([
    ...REQUIRED_PROPERTIES,
    "Pricing version",
    "Design reference",
    "Secure file reference",
    "_Pricing version",
    "_Design reference",
    "_Secure file reference",
    "Laminate price adjustment",
    "Laminate price multiplier",
    "Sticker total price",
    "_Sticker total price",
    "Price per sticker",
    "Premium Vinyl interpolated base",
    "Material price multiplier",
    "_CHAMELEON Pricing Input",
    "_CHAMELEON Deal Handle",
    "_CHAMELEON Deal Product ID",
    "_CHAMELEON Deal Variant ID",
    "Deal",
    "Deal terms",
    "Package",
    "Wrap quantity",
    "Package total price",
    "Price per pair",
    "Hole guide",
    "Shape",
    "Edge / bleed",
    "Edge thickness",
    "Border color",
    "_Border color",
    "Manual proof required",
    "Manual proof notes",
    "Proof approved by",
    "_Proof approved by",
    "_CHAMELEON Approved At",
    "_CHAMELEON Project ID",
    "_CHAMELEON Project Revision",
    "_CHAMELEON Layer Count",
    "_CHAMELEON Artwork SHA256",
    "_CHAMELEON Preview URL",
    "_CHAMELEON Preview Data",
    "_CHAMELEON Preview Alt",
    "_CHAMELEON Original Master SHA256",
    "_CHAMELEON Original Color Mode",
    "_CHAMELEON Studio Return URL",
    "Production fingerprint",
    "Production separations",
    "Added editable layers",
    "_Production fingerprint",
    "_Production separations",
    "_Added editable layers",
  ]);

  const root = window.Shopify?.routes?.root || "/";

  const rememberStudioQueue = () => {
    try {
      window.sessionStorage.setItem(
        RETURN_QUEUE_STORAGE_KEY,
        JSON.stringify({ url: window.location.href, savedAt: Date.now() }),
      );
    } catch {
      // Same-tab checkout still works if storage is unavailable.
    }
  };

  const openVerifiedCart = () => {
    const cartUrl = new URL(`${root}cart`, window.location.origin).toString();
    rememberStudioQueue();
    window.location.assign(cartUrl);
  };

  const fetchCart = async () => {
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error("Shopify could not return the cart for verification.");
    }

    return response.json();
  };

  const findVerifiedLine = async (jobId) => {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const cart = await fetchCart();
      const line = (cart.items || []).find(
        (item) =>
          item?.properties?.["_CHAMELEON Checkout Job ID"] === jobId,
      );

      if (line) return { cart, line };

      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }

    return { cart: await fetchCart(), line: null };
  };


  const removeUnsafeLine = async (lineKey) => {
    if (!lineKey) return;

    const response = await fetch(`${root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error(
        "Shopify returned an unsafe carrier price and the line could not be removed automatically. Please empty the cart before continuing.",
      );
    }
  };

  for (const block of blocks) {
    const frame = block.querySelector("[data-chameleon-studio-frame]");
    const configuredVariantId = Number(
      block.dataset.chameleonVariantId || 0,
    );
    const studioUrl =
      block.dataset.chameleonStudioUrl ||
      "https://studio.vinyl-disorder.com";
    const privateToken = block.dataset.chameleonPrivateToken || "";
    const privateParameter =
      block.dataset.chameleonPrivateParameter ||
      "chameleon_checkout";
    const privateMode = block.hasAttribute(
      "data-chameleon-private-studio",
    );
    let studioOrigin;

    try {
      studioOrigin = new URL(studioUrl, window.location.href).origin;
    } catch {
      continue;
    }

    if (
      !(frame instanceof HTMLIFrameElement) ||
      !Number.isSafeInteger(configuredVariantId) ||
      configuredVariantId <= 0
    ) {
      continue;
    }

    if (privateMode) {
      const supplied = new URLSearchParams(window.location.search).get(
        privateParameter,
      );

      if (!privateToken || supplied !== privateToken) {
        block.remove();
        continue;
      }

      block.hidden = false;
      document.documentElement.classList.add(
        "chameleon-private-checkout-open",
      );

      block
        .querySelector("[data-chameleon-private-close]")
        ?.addEventListener("click", () => {
          block.remove();
          document.documentElement.classList.remove(
            "chameleon-private-checkout-open",
          );
        });
    }

    const postToStudio = (message) => {
      frame.contentWindow?.postMessage(message, studioOrigin);
    };

    frame.addEventListener("load", () => {
      postToStudio({
        type: "CHAMELEON_SHOPIFY_HOST_READY",
        bridgeVersion: BRIDGE_VERSION,
        variantId: configuredVariantId,
        privateMode,
      });
    });

    window.addEventListener("message", async (event) => {
      if (
        event.source !== frame.contentWindow ||
        event.origin !== studioOrigin
      ) {
        return;
      }

      const data = event.data;

      if (!data) return;

      if (data.type === "CHAMELEON_STUDIO_HEIGHT") {
        if (!window.matchMedia("(min-width: 1181px)").matches) return;
        const requestedHeight = Number(data.height || 0);
        if (!Number.isFinite(requestedHeight) || requestedHeight < 520 || requestedHeight > 5000) return;
        const appliedHeight = Math.ceil(requestedHeight);
        block.dataset.chameleonContentHeight = String(appliedHeight);
        block.style.setProperty("--chameleon-studio-height", `${appliedHeight}px`);
        frame.setAttribute("scrolling", "no");
        postToStudio({ type: "CHAMELEON_STUDIO_HEIGHT_APPLIED", height: appliedHeight });
        return;
      }

      if (data.type === "CHAMELEON_CART_OPEN") {
        const requestId = String(data.requestId || "");
        const jobId = String(data.jobId || "");
        const lineKey = String(data.lineKey || "");
        const reply = (ok, message = "") =>
          postToStudio({
            type: "CHAMELEON_CART_OPEN_RESULT",
            requestId,
            ok,
            message,
            jobId,
            lineKey,
          });

        if (!requestId || !jobId || !lineKey || data.bridgeVersion !== BRIDGE_VERSION) {
          reply(false, "The CHAMELEON checkout bridge request is not valid.");
          return;
        }

        try {
          const { line } = await findVerifiedLine(jobId);
          if (!line || String(line.key || "") !== lineKey) {
            throw new Error("The approved design is no longer in this Shopify cart.");
          }
          reply(true);
          window.setTimeout(openVerifiedCart, 60);
        } catch (error) {
          reply(false, error instanceof Error ? error.message : "Shopify could not verify the cart.");
        }
        return;
      }

      if (data.type !== "CHAMELEON_CART_ADD") return;

      const requestId = String(data.requestId || "");
      const requestedVariantId = Number(data.variantId || 0);
      const sourceProperties =
        data.properties && typeof data.properties === "object"
          ? data.properties
          : {};

      const fail = (message) =>
        postToStudio({
          type: "CHAMELEON_CART_RESULT",
          requestId,
          ok: false,
          message,
        });

      if (!requestId || data.bridgeVersion !== BRIDGE_VERSION) {
        fail("The CHAMELEON checkout bridge version is not valid.");
        return;
      }

      if (requestedVariantId !== configuredVariantId) {
        fail(
          "The Custom Sticker variant does not match the verified Shopify carrier.",
        );
        return;
      }

      const properties = {};

      for (const [name, value] of Object.entries(sourceProperties)) {
        if (!ALLOWED_PROPERTIES.has(name)) continue;
        properties[name] = String(value ?? "").slice(0, 28000);
      }

      const missing = requiredPropertiesFor(properties).filter(
        (name) => !properties[name],
      );

      if (missing.length > 0) {
        fail(
          `The approved design is missing required production data: ${missing.join(", ")}.`,
        );
        return;
      }

      const jobId = properties["_CHAMELEON Checkout Job ID"];
      const expectedCents = Number.parseInt(
        properties["_CHAMELEON Intended Price Cents"],
        10,
      );
      const proofStatus =
        properties["_CHAMELEON Proof Status"];

      if (
        !jobId ||
        jobId.length > 160 ||
        !Number.isSafeInteger(expectedCents) ||
        expectedCents <= 0 ||
        expectedCents > 1000000000
      ) {
        fail("The CHAMELEON checkout job or expected price is invalid.");
        return;
      }

      if (!["approved", "manual"].includes(proofStatus)) {
        fail(
          "Approve the proof or request Manual Proof before adding this design.",
        );
        return;
      }

      if (
        properties["_CHAMELEON Cart Bridge Version"] !==
        BRIDGE_VERSION
      ) {
        fail("The line-item bridge version does not match the host.");
        return;
      }

      const compactAuthorization = JSON.stringify({
        v: properties["_CHAMELEON Cart Bridge Version"],
        d: properties["_CHAMELEON Design ID"],
        f: properties["_CHAMELEON Proof Fingerprint"],
        s: properties["_CHAMELEON Proof Status"],
        w: properties["_CHAMELEON Width"],
        h: properties["_CHAMELEON Height"],
        q: properties["_CHAMELEON Sticker Quantity"],
        m: properties["_CHAMELEON Material"],
        l: properties["_CHAMELEON Laminate"],
        c: properties["_CHAMELEON Intended Price Cents"],
        o: properties["_CHAMELEON Original Master SHA256"],
        x: properties["_CHAMELEON Original Color Mode"],
        p: properties["_Pricing version"] || properties["Pricing version"],
        e: properties["_CHAMELEON Deal Handle"] || "",
      });
      const checkoutProperties = {};
      const staffProperties = [
        "Finished size",
        "Material",
        "Laminate",
        "Shape",
        "Proof status",
        "Sticker quantity",
        "Price per sticker",
        "Manual proof notes",
        "Package",
        "Wrap quantity",
        "Package total price",
        "Price per pair",
        "Hole guide",
        "Deal",
        "Deal terms",
      ];
      staffProperties.forEach((name) => {
        if (properties[name]) checkoutProperties[name] = properties[name];
      });
      checkoutProperties["_CHAMELEON Checkout Job ID"] = jobId;
      checkoutProperties["_CHAMELEON Authorization"] = compactAuthorization;
      checkoutProperties["_CHAMELEON Studio Return URL"] =
        properties["_CHAMELEON Studio Return URL"] || `${studioOrigin}/create`;
      checkoutProperties["_CHAMELEON Preview URL"] =
        properties["_CHAMELEON Preview URL"] ||
        `${studioOrigin}/api/intake/jobs/${encodeURIComponent(jobId)}/preview`;
      if (properties["_CHAMELEON Preview Alt"]) {
        checkoutProperties["_CHAMELEON Preview Alt"] =
          properties["_CHAMELEON Preview Alt"];
      }
      [
        "_CHAMELEON Width",
        "_CHAMELEON Height",
        "_CHAMELEON Sticker Quantity",
        "_CHAMELEON Material",
        "_CHAMELEON Laminate",
        "_CHAMELEON Intended Price Cents",
        "_CHAMELEON Cart Bridge Version",
        "_CHAMELEON Deal Handle",
      ].forEach((name) => {
        if (properties[name]) checkoutProperties[name] = properties[name];
      });
      checkoutProperties["Pricing version"] =
        properties["_Pricing version"] || properties["Pricing version"];

      try {
        const addResponse = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: configuredVariantId,
                quantity: 1,
                properties: checkoutProperties,
              },
            ],
          }),
        });

        const addBody = await addResponse.json().catch(() => null);

        if (!addResponse.ok) {
          throw new Error(
            addBody?.description ||
              addBody?.message ||
              "Shopify could not add this design to the cart.",
          );
        }

        const { cart, line } = await findVerifiedLine(jobId);

        if (!line) {
          throw new Error(
            "Shopify added the item but the verified CHAMELEON job could not be found.",
          );
        }

        const actualCents = Number(line.final_line_price);
        const currency = String(cart.currency || "USD");

        if (actualCents !== expectedCents) {
          await removeUnsafeLine(line.key);
          throw new Error(
            `Shopify blocked this cart line because the verified price did not apply. The unsafe carrier-price line was removed automatically. Expected ${currency} ${(expectedCents / 100).toFixed(2)}.`,
          );
        }

        const lineProperties = line.properties || {};
        const missingAfterAdd = Object.keys(checkoutProperties).filter(
          (name) => !lineProperties[name],
        );

        if (missingAfterAdd.length > 0) {
          await removeUnsafeLine(line.key);
          throw new Error(
            `Shopify dropped required production data and the incomplete cart line was removed: ${missingAfterAdd.join(", ")}.`,
          );
        }

        postToStudio({
          type: "CHAMELEON_CART_RESULT",
          requestId,
          ok: true,
          jobId,
          lineKey: line.key,
          actualFinalLinePriceCents: actualCents,
          currency,
        });

        if (data.redirectToCart !== false) {
          window.setTimeout(openVerifiedCart, 60);
        }
      } catch (error) {
        fail(
          error instanceof Error
            ? error.message
            : "Shopify could not add this approved design to the cart.",
        );
      }
    });
  }
})();



/* CHAMELEON_ANDROID_PARENT_PAGE_SCROLL_HANDOFF */
(() => {
  const blocks = Array.from(document.querySelectorAll("[data-chameleon-shopify-studio]:not([data-chameleon-private-studio])"));
  if (blocks.length === 0) return;

  const frameForSource = (source) => blocks
    .map((block) => ({ block, frame: block.querySelector("iframe") }))
    .find(({ frame }) => frame?.contentWindow === source);

  const postScrollState = (entry) => {
    if (!entry?.frame?.contentWindow) return;
    let targetOrigin = "*";
    try {
      targetOrigin = new URL(entry.frame.src, window.location.href).origin;
    } catch {
      targetOrigin = "*";
    }
    const visualViewport = window.visualViewport;
    const viewportTop = Number(visualViewport?.offsetTop || 0);
    entry.frame.contentWindow.postMessage({
      type: "CHAMELEON_PAGE_SCROLL_STATE",
      frameTop: entry.frame.getBoundingClientRect().top - viewportTop,
      pageScrollY: window.scrollY,
      viewportHeight: Number(visualViewport?.height || window.innerHeight)
    }, targetOrigin);
  };

  const reportAllScrollStates = () => {
    blocks.forEach((block) => postScrollState({ block, frame: block.querySelector("iframe") }));
  };

  window.addEventListener("message", (event) => {
    const entry = frameForSource(event.source);
    if (!entry?.frame) return;
    let expectedOrigin = "";
    try {
      expectedOrigin = new URL(entry.frame.src, window.location.href).origin;
    } catch {
      return;
    }
    if (event.origin !== expectedOrigin) return;

    if (event.data?.type === "CHAMELEON_PAGE_SCROLL_STATE_REQUEST") {
      postScrollState(entry);
      return;
    }
    if (event.data?.type !== "CHAMELEON_PAGE_SCROLL_DELTA") return;

    const deltaY = Number(event.data.deltaY || 0);
    if (!Number.isFinite(deltaY) || Math.abs(deltaY) > 160) return;
    const frameTop = entry.frame.getBoundingClientRect().top - Number(window.visualViewport?.offsetTop || 0);
    const allowedDelta = deltaY > 0
      ? Math.min(deltaY, Math.max(0, frameTop))
      : Math.max(deltaY, -window.scrollY);
    if (allowedDelta === 0) {
      postScrollState(entry);
      return;
    }
    window.scrollBy(0, allowedDelta);
    window.requestAnimationFrame(() => postScrollState(entry));
  });

  window.addEventListener("scroll", reportAllScrollStates, { passive: true });
  window.addEventListener("resize", reportAllScrollStates, { passive: true });
  window.visualViewport?.addEventListener("resize", reportAllScrollStates, { passive: true });
  window.visualViewport?.addEventListener("scroll", reportAllScrollStates, { passive: true });
  blocks.forEach((block) => block.querySelector("iframe")?.addEventListener("load", reportAllScrollStates));
  window.setTimeout(reportAllScrollStates, 0);
  window.setTimeout(reportAllScrollStates, 500);
})();
